import {
	AddLocationAlt,
	CachedOutlined,
	Delete,
	Download,
	DownloadOutlined,
	DriveFileRenameOutline,
	Edit,
	GpsFixedTwoTone,
	ListOutlined,
	MapOutlined,
	Report,
	SearchOutlined,
	UploadFileOutlined,
    ArrowForwardIos,
    ArrowBackIosNew
} from "@mui/icons-material";
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	FormControlLabel,
	IconButton,
	InputAdornment,
	OutlinedInput,
	Portal,
	Stack,
	Tooltip,
	Typography,
} from "@mui/material";
import { FeatureLike } from "ol/Feature";
import "ol/ol.css";
import { extend, createEmpty, isEmpty } from "ol/extent";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { uploadShapefileESR } from "../../../actions/form";
import getBackendURL from "../../../api/api";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { download } from "../../../request";
import { ESRFieldProps, ESRState } from "../../../types/data";
import { ButtonMenu } from "../../ButtonMenu";
import { useQuestionLayer } from "./hooks/layers/useQuestionLayer";
import { parsePOIs, POI, usePOILayer } from "./hooks/layers/usePOILayer";
import { parseCountyGeoids, useCountyLayer } from "./hooks/layers/useCountyLayer";
import { CountyPicker } from "./CountyPicker";
import { usePOIClick } from "./hooks/interactions/usePOIClick";
import { usePOIOverlay } from "./hooks/highlight/usePOIOverlay";
import { POIEditor } from "./POIEditor";
import { fromLonLat, toLonLat } from "ol/proj";
import { Coordinate } from "ol/coordinate";
import { Feature } from "ol";
import { useReferenceLayer } from "./hooks/layers/useReferenceLayer";
import { useSearch } from "./hooks/search/useSearch";
import { useInteractions } from "./hooks/useInteractions";
import { useMap } from "./hooks/useMap";
import { useAPI } from "./hooks/utils/useAPI";
import { useShapeHistory } from "./hooks/utils/useShapeHistory";
import { ShapeTimeline } from "./ShapeTimeline";
import {
	colorMap,
	getGroupColor,
	getQuestionColor,
	getReferenceColor,
	PRIMARY_COLOR,
} from "./styles";
import {useParams} from "react-router-dom";
import {useRemoteField} from "../../../hooks/useRemoteField";
import { isValidId } from "@/actions/util";

const groupDisplayText: Record<string, string> = {
	project_limits: "Project Limits",
	state_row: "State ROW",
	nat_species: "Natural Species",
	Bat_Buffer: "Half-Mile Bat Hibernacula Buffer ",
	High_Potential_Zones: "Rusty Patched Bumble Bee HPZ",
	Hydric_NP: "Nature Preserve",
	Hydric_Soils: "Hydric Soils",
	INAI: "IL Natural Areas Inventory",
	National_Rivers_Inventory: "National Rivers Inventory",
	PLSS: "PLSS",
	Roadside_Prairie_Inventory: "Roadside Prairie Inventory ",
	Wet_Poly: "National Wetlands Inventory",
	Wetland: "Delineated Wetlands",
	Threatened_Endangered_Species_Buffers: "Threatened & Endangered Species Buffers",
};

export const ESRMapSelectorField = (
	props: ESRFieldProps & { simple: boolean },
) => {
	const { esrId = "", activeId = "", activeLabel = "" } = useParams();
	const { question_id, locked, inline, portalContainerEl, simple } = props;
	const responses = useSelector((state: ESRState) => state.responses);
	const addendums = useSelector((state: ESRState) => state.addendums);
	const [value] = useRemoteField(question_id, "");
	const [user] = useCurrentUser();

	const dispatch = useDispatch();

	const shapeVersion = useSelector((state: ESRState) => state.shapeVersion);

	const [open, setOpen] = useState(false);
	const [viewMode, setViewMode] = useState<"map" | "list">("map");
	const [lastClicked, setLastClicked] = useState("none");
	const [countyPickerOpen, setCountyPickerOpen] = useState(false);

	// from the original `ESRMapFileUploadField`
	const uploadButtonRef = useRef<HTMLInputElement | null>(null);

	const loading = useSelector((state: ESRState) => state.loading[question_id]);

	const handleOnChange = (e: ChangeEvent<HTMLInputElement>) => {
		if (e.target.files?.[0]) {
			if (user !== undefined && user !== null) {
				dispatch(
					uploadShapefileESR(
						esrId,
						activeLabel,
						activeId,
						question_id,
						e.target.files[0],
						user.email,
					),
				);
			}
		}
		e.target.value = ""; // clear file list
	};

	const triggerNativeUploadPopup = useCallback(() => {
		// TODO: Remove existing TREC-added shapes before handling
		uploadButtonRef.current?.click();
		setViewMode("map");
	}, []);

	const showReferenceAndLegend = !simple;

	const { updateShapesInDB, deleteShapeInDB } = useAPI(activeId, question_id);

	const { map, mapContainerRef } = useMap(open);

	const [viewingId, setViewingId] = useState(activeId);
	useEffect(() => {
		setViewingId(activeId);
	}, [activeId]);

	const [visibility, setVisibility] = useState<Record<string, boolean>>({});
	const visible = useCallback(
		(feature: FeatureLike) => {
			const group = feature.get("group");
			return visibility[group] !== false;
		},
		[visibility],
	);

	const {
		source: questionSource,
		layer: questionLayer,
		hargisUrl,
		plssUrl,
		mapLoading: questionMapLoading,
		showingFrom: questionFrom,
		shown,
		shapeIndex,
		shapeCount,
		currentShape,
		nextShape,
		previousShape,
		showAllShapes,
	} = useQuestionLayer(
		map,
		viewingId,
		question_id,
		shapeVersion,
		visible,
		getQuestionColor(question_id),
	);

	const {
		source: referenceSource,
		mapLoading: referenceMapLoading,
		showingFrom: referenceFrom,
	} = useReferenceLayer(
		map,
		viewingId,
		questionFrom,
		question_id,
		shapeVersion,
		showReferenceAndLegend,
	);

	// A row per question on the map — one when the reference is an earlier
	// version of this question, two when it is the project limits underneath.
	// C82M has no addendum history of its own to show: it's synthesized from
	// each C82 row's independently-versioned shapes, so no single per-addendum
	// "has shapes" bit for it would be accurate.
	const shapeHistory = useShapeHistory(
		activeId,
		question_id === "E92"
			? ["E92"]
			: question_id === "C82M"
				? []
				: [question_id, "E92"],
		shapeVersion,
	);

	// `useAPI` only ever writes to activeId. Creating adds to whatever is being
	// viewed and editing changes whatever was resolved, so each is allowed only
	// when that ESR is the active one.
	const canCreate = viewingId === activeId;
	const canEditExisting =
		questionFrom === activeId && questionSource.getFeatures().length > 0;

	// Points of interest live in the active ESR's `<qid>.POI` response, so they
	// are only shown while that ESR is the one being viewed
	const [poiValue, setPoiValue] = useRemoteField(`${question_id}.POI`, "");
	const pois = useMemo(
		() =>
			question_id === "E92" && viewingId === activeId ? parsePOIs(poiValue) : [],
		[question_id, viewingId, activeId, poiValue],
	);
	const {
		draw,
		isEditing,
		isDeleting,
		setIsDeleting,
		setSelectedFeature,
		setDrawEnabled,
		setModifyEnabled,
		poiEnabled,
		setPoiEnabled,
		highlightedFeature,
		overlayElRef,
	} = useInteractions(
		map,
		questionSource,
		questionLayer,
		shown,
		updateShapesInDB,
	);
	const { layer: poiLayer } = usePOILayer(
		map,
		pois,
		poiEnabled || canEditExisting,
	);

	const [countiesValue, setCountiesValue] = useRemoteField(`${question_id}.COUNTIES`, "");
	const countyGeoids = useMemo(
		() =>
			question_id === "E92" && viewingId === activeId ? parseCountyGeoids(countiesValue) : [],
		[question_id, viewingId, activeId, countiesValue],
	);
	useCountyLayer(map, countyGeoids);

	// whichever versions are on the map right now
	const downloadActions = [
		{
			title: `Download ${question_id} Shapes`,
			esrId: questionFrom,
			questionId: question_id,
		},
		{
			title: `Download ${question_id === "E92" ? "Previous E92" : "E92"} Shapes`,
			esrId: referenceFrom,
			questionId: "E92",
		},
	]
		.filter(({ esrId }) => isValidId(esrId))
		.map(({ title, esrId, questionId }) => ({
			title,
			action: () =>
				download(getBackendURL(`/esr/download_geodata/${esrId}/${questionId}/`)),
		}));

	const fitView = useCallback(() => {
		if (!map) return;
		const combined = createEmpty();
		// stepping through shapes focuses on the one on show
		const extents = currentShape
			? [currentShape.getGeometry()?.getExtent() ?? createEmpty()]
			: [questionSource.getExtent(), referenceSource.getExtent()];
		for (const ext of extents) {
			if (ext.every(Number.isFinite)) extend(combined, ext);
		}
		if (!isEmpty(combined)) {
			map.getView().fit(combined, { padding: [50, 50, 50, 50] });
		}
	}, [map, questionSource, referenceSource, currentShape]);

	const { currentSearch, setCurrentSearch } = useSearch(map);
	const updateGeoSearch = useCallback((e: ChangeEvent<HTMLInputElement>) => {
		setCurrentSearch(e.target.value);
	}, []);


	const highlightedFeatureId = highlightedFeature
		? (highlightedFeature.get("identifier") ?? String(highlightedFeature.getId()))
		: "";

	// The point of interest the editor is open for. Clicking empty map creates a
	// point right away; a misplaced one has to be deleted explicitly.
	const [poiTarget, setPoiTarget] = useState<(POI & { index: number }) | null>(null);

	const onPOIClick = useCallback(
		(coordinate: Coordinate, feature: Feature | null) => {
			if (feature) {
				const index: number = feature.get("index");
				setPoiTarget({ ...pois[index], index });
			} else {
				const [lon, lat] = toLonLat(coordinate);
				const poi = { label: "", lon, lat };
				setPoiValue(JSON.stringify([...pois, poi]));
				setPoiTarget({ ...poi, index: pois.length });
			}
		},
		[pois, setPoiValue],
	);
	usePOIClick(map, poiLayer, poiEnabled, onPOIClick);

	const poiPosition = useMemo(
		() => (poiTarget ? fromLonLat([poiTarget.lon, poiTarget.lat]) : undefined),
		[poiTarget],
	);
	const { poiOverlayElRef } = usePOIOverlay(map, poiPosition);

	const savePOI = (label: string) => {
		if (!poiTarget) return;
		const next = [...pois];
		next[poiTarget.index] = { label, lon: poiTarget.lon, lat: poiTarget.lat };
		setPoiValue(JSON.stringify(next));
		setPoiTarget(null);
	};

	const deletePOI = () => {
		if (!poiTarget || poiTarget.index === null) return;
		const next = pois.filter((_, i) => i !== poiTarget.index);
		setPoiValue(JSON.stringify(next));
		setPoiTarget(null);
	};

	const isEditingRef = useRef(isEditing);
	useEffect(() => { isEditingRef.current = isEditing; }, [isEditing]);

	// OL-driven auto-fit: subscribes to both source change events so fitView
	// triggers when either the question or reference layer loads/changes.
	useEffect(() => {
		const fit = () => { if (!isEditingRef.current) fitView(); };
		fit();
		questionSource.on("change", fit);
		referenceSource.on("change", fit);
		return () => {
			questionSource.un("change", fit);
			referenceSource.un("change", fit);
		};
	}, [questionSource, referenceSource, fitView]);

	const [referenceGroups, setReferenceGroups] = useState<Set<string>>(new Set());
	const [questionGroups, setQuestionGroups] = useState<Set<string>>(new Set());

	useEffect(() => {
		const updateRefGroups = () => {
			setReferenceGroups(
				new Set(referenceSource.getFeatures().map((f) => f.get("group") as string))
			);
		};
		const updateQGroups = () => {
			setQuestionGroups(
				new Set(questionSource.getFeatures().map((f) => f.get("group") as string))
			);
		};
		updateRefGroups();
		updateQGroups();
		referenceSource.on("change", updateRefGroups);
		questionSource.on("change", updateQGroups);
		return () => {
			referenceSource.un("change", updateRefGroups);
			questionSource.un("change", updateQGroups);
		};
	}, [referenceSource, questionSource]);

	useEffect(() => {
		setIsDeleting(false);
		setSelectedFeature(null);
	}, [activeId, question_id]);

	const startModify = useCallback(() => {
		setViewMode("map");
		setLastClicked("modify");
		setModifyEnabled(true);
		setDrawEnabled(false);
	}, []);

	const startDraw = useCallback(() => {
		setViewMode("map");
		setLastClicked("draw");
		// draw among all the shapes, so a new one isn't hidden as "not the current one"
		showAllShapes();
		setDrawEnabled(true);
		setModifyEnabled(false);
	}, [showAllShapes]);

	const startPOI = useCallback(() => {
		setViewMode("map");
		setLastClicked("poi");
		setPoiEnabled(true);
	}, []);

	const done = useCallback(() => {
		if (lastClicked == "modify") {
			updateShapesInDB(questionSource.getFeatures());
		}
		setDrawEnabled(false);
		setModifyEnabled(false);
		setPoiEnabled(false);
		setPoiTarget(null);
	}, [questionSource, updateShapesInDB, lastClicked]);

	const deleteShape = useCallback(
		(identifier: string) => {
			deleteShapeInDB(identifier);
			setSelectedFeature(null);
		},
		[deleteShapeInDB, setSelectedFeature],
	);

	const mapInputElement = (
		<>
			{/* a hidden input that handles file uploads */}
			<input
				id={question_id}
				ref={uploadButtonRef}
				type="file"
				accept=".zip,.kmz"
				style={{
					clipPath: "inset(50%)",
					height: 1,
					width: 1,
				}}
				onChange={handleOnChange}
			/>
			<CountyPicker
				open={countyPickerOpen}
				onClose={() => setCountyPickerOpen(false)}
				geoids={countyGeoids}
				onSave={(geoids) => setCountiesValue(JSON.stringify(geoids))}
			/>

			<Box
				sx={{
					margin: "10px 0",
					display: "flex",
					flexDirection: "column",
					gap: "10px",
				}}
			>
				{question_id == "E92" && responses["E92A"]?.response === 'False' && !value && (
					<Typography color="error">
						{responses["E92A"].responder_id === "TREC"
							? "Per question E92A, the project bounds must be drawn or uploaded before this addendum can be submitted."
							: "Per question E92A, the previous project bounds must be modified before this addendum can be submitted."}
					</Typography>
				)}
				{question_id == "E92D" && responses["E92CA"]?.response === 'False' && !value && (
					<Typography color="error">
						{responses["E92CA"].responder_id === "TREC"
							? "Per question E92CA, the state ROW must be drawn or uploaded before this addendum can be submitted."
							: "Per question E92CA, the previous state ROW must be modified before this addendum can be submitted."}
					</Typography>
				)}
				<Box sx={{ display: "flex", gap: "12px" }}>
					<ButtonMenu
						disabled={locked > 0 || isEditing || !canCreate}
						title="Upload Shapefile or Draw Polygon"
						icon={
							loading ? (
								<CircularProgress size={20} sx={{ color: "white" }} />
							) : (
								<UploadFileOutlined />
							)
						}
						actions={[
							{
								title: "Upload Shapefile",
								action: triggerNativeUploadPopup,
							},
							{
								title: "Draw New Polygons",
								action: startDraw,
							},
							...(question_id === "E92"
								? [{ title: "Specify by County", action: () => setCountyPickerOpen(true) }]
								: []),
						]}
					/>
					<Button
						variant="contained"
						startIcon={<Edit />}
						disabled={locked > 0 || isEditing || !canEditExisting}
						onClick={startModify}
					>
						Edit Shapes
					</Button>
					{question_id === "E92" && (
						<Button
							variant="contained"
							startIcon={<AddLocationAlt />}
							disabled={locked > 0 || isEditing || !canCreate}
							onClick={startPOI}
						>
							Mark/Edit Points of Interest
						</Button>
					)}
					<ButtonMenu
						disabled={isEditing}
						title="Download Shapefile"
						icon={<DownloadOutlined />}
						actions={downloadActions}
					/>
					<Box sx={{ flex: 1 }} />
					{viewMode === "map" ? (
						<Button
							variant="text"
							disabled={isEditing}
							onClick={() => setViewMode("list")}
							startIcon={<ListOutlined />}
						>
							View As List
						</Button>
					) : (
						<Button
							variant="text"
							disabled={isEditing}
							onClick={() => setViewMode("map")}
							startIcon={<MapOutlined />}
						>
							View in Map
						</Button>
					)}
				</Box>

				{/* fixed so the timeline above grows the container rather than
				    squeezing the map */}
				<Box sx={{ position: "relative", height: 670 }}>
					{(loading || referenceMapLoading || questionMapLoading) && (
						<Stack
							sx={{
								position: "absolute",
								left: 0,
								right: 0,
								top: 0,
								bottom: 0,
								zIndex: 1,
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<CircularProgress size={100} />
						</Stack>
					)}
					<Box sx={{ height: "100%", opacity: viewMode === "map" ? 1 : 0 }}>
						<Box
							tabIndex={0}
							onKeyUp={(e) => {
								if (e.key === "Backspace") {
									draw.removeLastPoint();
								}
							}}
							ref={mapContainerRef}
							sx={{
								width: "100%",
								height: "100%",
								"& .ol-zoom": {
									top: "auto",
									left: "auto",
									right: "8px",
									bottom: "36px",
								},
								"& .ol-scale-line": {
									left: "auto",
									right: "8px",
									bottom: "8px",
								},
							}}
						/>
						{showReferenceAndLegend && (
							<Box
								id="legend"
								sx={{
									position: "absolute",
									bottom: 0,
									left: 0,
									background: "#ffffffcc",
									padding: "10px",
								}}
							>
								{Object.entries(colorMap)
									.filter(([group]) => referenceGroups.has(group))
									.map(([group, color]) => (
										<Box key={group} sx={{ px: "8px" }}>
											<FormControlLabel
												control={
													<Checkbox
														sx={{ p: 0 }}
														size="small"
														checked={true}
														disabled={true}
													/>
												}
												label={
													<Box>
														<Box
															sx={{
																display: "inline-block",
																verticalAlign: "middle",
																width: 32,
																height: 16,
																mx: "8px",
																background: getReferenceColor(question_id, "E92"),
															}}
														/>
														<Typography
															sx={{
																display: "inline-block",
																verticalAlign: "middle",
																fontSize: 14,
																userSelect: "none",
															}}
														>
															{groupDisplayText[group]} {question_id != "E92" ? "" : "(Previous)"}
														</Typography>
													</Box>
												}
											/>
										</Box>
									))}

								{[...questionGroups]
									.map((group) => [group, getGroupColor(group)] as const)
									.filter(([, color]) => color !== undefined)
									.map(([group, color]) => (
										<Box key={group} sx={{ px: "8px" }}>
											<FormControlLabel
												control={
													<Checkbox
														sx={{ p: 0 }}
														size="small"
														checked={visibility[group] !== false}
														onChange={() => {
															setVisibility({
																...visibility,
																[group]: visibility[group] === false, // undefined => false, true => false, false => true
															});
														}}
													/>
												}
												label={
													<Box>
														<Box
															sx={{
																display: "inline-block",
																verticalAlign: "middle",
																width: 32,
																height: 16,
																mx: "8px",
																background: color + "aa",
															}}
														/>
														<Typography
															sx={{
																display: "inline-block",
																verticalAlign: "middle",
																fontSize: 14,
																userSelect: "none",
															}}
														>
															{groupDisplayText[group] ?? group}
														</Typography>
													</Box>
												}
											/>
										</Box>
									))}
							</Box>
						)}
						<Box
							sx={{
								position: "absolute",
								top: 18,
								left: 18,
								display: "flex",
								alignItems: "center",
								gap: "12px",
							}}
						>
							<OutlinedInput
								inputProps={{
									"aria-label": "Search Location",
								}}
								size="small"
								sx={{ background: "white", width: 220 }}
								placeholder="Search Location"
								endAdornment={
									<InputAdornment position="end">
										<SearchOutlined />
									</InputAdornment>
								}
								value={currentSearch}
								onChange={updateGeoSearch}
							></OutlinedInput>
							<Button
								aria-label="reset view"
								variant="contained"
								sx={{ minWidth: 0, p: "6px" }}
								onClick={fitView}
							>
								<Tooltip title="Reset View">
									<CachedOutlined />
								</Tooltip>
							</Button>
							<ButtonMenu
								disabled={isEditing}
								sx={{ p: "6px" }}
								title={
									<Tooltip title="Locate">
										<GpsFixedTwoTone />
									</Tooltip>
								}
								actions={[
									{
										title: "Locate in HARGIS",
										action: () => window.open(hargisUrl, "_blank"),
									},
									{
										title: "Locate in PSS",
										action: () => window.open(plssUrl, "_blank"),
									},
								]}
							/>
                            <Button
                                aria-label="previous"
                                variant="contained"
                                sx={{minWidth: 0, p: "6px"}}
                                onClick={previousShape}
                                disabled={shapeCount < 2}
                            >
                                <Tooltip title="Previous Shape">
                                    <ArrowBackIosNew/>
                                </Tooltip>
                            </Button>

                            <Typography
                                aria-live="polite"
                                sx={{
                                    background: "white",
                                    borderRadius: "4px",
                                    px: "10px",
                                    py: "6px",
                                    fontSize: 14,
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {shapeIndex === -1 || shapeCount === 0
                                    ? "Showing All"
                                    : `Showing ${(shapeIndex % shapeCount) + 1} of ${shapeCount}`}
                            </Typography>

                            <Button
                                aria-label="next"
                                variant="contained"
                                sx={{minWidth: 0, p: "6px"}}
                                onClick={nextShape}
                                disabled={shapeCount < 2}
                            >
                                <Tooltip title="Next Shape">
                                    <ArrowForwardIos/>
                                </Tooltip>
                            </Button>
						</Box>
						{isEditing ? (
							<Button
								variant="contained"
								sx={{ position: "absolute", top: 18, right: 18 }}
								onClick={done}
							>
								Done
							</Button>
						) : null}
					</Box>

					{viewMode === "list" ? (
						<Box
							sx={{
								position: "absolute",
								top: 0,
								bottom: 0,
								left: 0,
								right: 0,
								p: "30px",
								borderRadius: "12px",
								display: "flex",
								flexWrap: "wrap",
								alignContent: "start",
								rowGap: "12px",
								columnGap: "18px",
								background: "#003E620A",
								overflow: "scroll",
							}}
						>
							<Typography fontSize={16} fontWeight={600} sx={{ width: "100%", color: "#003E62" }}>
								Showing all shapes
							</Typography>
							{questionSource.getFeatures().map((feature, index) => {
								const id = feature.get("identifier") ?? String(feature.getId());
								return id ? (
									<Box
										key={`${index}-${id}`}
										sx={{
											boxSizing: "border-box",
											width: "calc((100% - 18px) / 2)",
											flex: "none",
											padding: "16px",
											background: "white",
											":hover": {
												background: "#eee",
											},
											transition: "all 0.2s",
											borderRadius: "6px",
											display: "flex",
											alignItems: "center",
											gap: "16px",
										}}
									>
										{String(id).startsWith("BD_") ? (
											<DriveFileRenameOutline sx={{ color: "#003E628A" }} />
										) : (
											<UploadFileOutlined sx={{ color: "#003E628A" }} />
										)}
										<Typography
											fontSize={14}
											fontWeight={500}
											flex="auto"
											sx={{
												minWidth: 0,
												overflow: "hidden",
												textOverflow: "ellipsis",
											}}
										>
											<strong>{feature.get("group") || "Project"}/</strong>
											{id}
										</Typography>
										<IconButton
											aria-label="download shape"
											onClick={() => {
												const group = feature.get("group");
												const groupParam = group ? `&group=${encodeURIComponent(group)}` : "";
												download(
													getBackendURL(
														`/esr/download_geodata/${activeId}/${question_id}/?identifier=${encodeURIComponent(id)}${groupParam}`,
													),
												);
											}}
										>
											<Download color="info" />
										</IconButton>
										<IconButton
											aria-label="delete shape"
											disabled={locked === 2 || !canEditExisting}
											onClick={() => deleteShape(String(id))}
										>
											<Delete
												color={locked === 2 || !canEditExisting ? "disabled" : "error"}
											/>
										</IconButton>
									</Box>
								) : null;
							})}
						</Box>
					) : null}
				</Box>

				<ShapeTimeline
					history={shapeHistory}
					questionId={question_id}
					questionFrom={questionFrom}
					referenceQuestionId="E92"
					referenceFrom={referenceFrom}
					viewingId={viewingId}
					onSelect={setViewingId}
				/>

				<Box
					sx={{
						p: "2px 4px",
						background: PRIMARY_COLOR,
						fontSize: 14,
						fontFamily: "Roboto, sans-serif",
						display: "flex",
						alignItems: "center",
					}}
					ref={overlayElRef}
				>
					{highlightedFeature?.get("group") ? (
						<Report />
					) : highlightedFeatureId.startsWith?.("BD_") ? (
						<DriveFileRenameOutline />
					) : (
						<UploadFileOutlined />
					)}
					<Typography
						fontSize={14}
						fontWeight={400}
						sx={{ ml: "2px", mr: "12px" }}
					>
						<strong>{highlightedFeature?.get("group") || "Project"}/</strong>
						{highlightedFeatureId}
					</Typography>
					{(locked !== 2) && canEditExisting && highlightedFeatureId ? (
						<IconButton
							aria-label="delete shape"
							sx={{ p: 0 }}
							onClick={() => {
								setIsDeleting(true);
								deleteShape(highlightedFeatureId);
							}}
						>
							<Delete color="error" />
						</IconButton>
					) : null}
				</Box>

				<Alert severity="info" sx={{ opacity: isEditing ? 1 : 0 }}>
					{poiEnabled
						? "Click the map to add a point of interest, or click a point to rename or delete it. Click “Done” when finished."
						: "Please click and adjust the dots to generate a shape. Click “Done” to confirm the shape."}
				</Alert>

				<Box ref={poiOverlayElRef}>
					{poiTarget && (
						<POIEditor
							key={poiTarget.index}
							initialLabel={poiTarget.label}
							onSave={savePOI}
							onDelete={deletePOI}
						/>
					)}
				</Box>
			</Box>
		</>
	);

	const hasResponse = questionSource.getFeatures().length > 0;
	const isC82MapQuestion = question_id.startsWith("C82.") && question_id.endsWith(".MAP");
	const showMapButton = isC82MapQuestion || hasResponse;
	return inline ? (
		<>
			{showMapButton ? (
				<Button onClick={() => setOpen(!open)}>
					{open ? "Hide" : "Show"} Map
				</Button>
			) : (
				<Button
					variant="contained"
					disabled={locked > 0 || isEditing || !canCreate}
					title="Upload Shapefile or Draw Polygon"
					startIcon={
						loading ? (
							<CircularProgress size={20} sx={{ color: "white" }} />
						) : (
							<UploadFileOutlined />
						)
					}
					onClick={() => {
						triggerNativeUploadPopup();
					}}
				>
					Upload Shapefile
				</Button>
			)}
			<Portal container={portalContainerEl}>
				<Box
					sx={{
						height: (hasResponse && open) || (isC82MapQuestion && open)? 700 : 0,
						overflow: "hidden",
						boxSizing: "border-box",
						width:
							portalContainerEl?.closest("table")?.parentElement?.clientWidth ??
							700,
						position: "sticky",
						left: 0,
						px: "40px",
					}}
				>
					{mapInputElement}
				</Box>
			</Portal>
		</>
	) : (
		mapInputElement
	);
};
